import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {useDispatch} from "react-redux";
import {useEffect, useMemo, useState} from "react";
import routes from "../../../Routes/routes";
import {STEP_MODEL} from "../Model/HomeModel";
import {errorMsg, isEmailFormat} from "../../../Apis/functions";
import {NewPassword, SendEmailToResetPassword, VerifyOtp
} from "../../../reduxToolkit/authentication/AuthenticatonApi";
import {toast} from "react-toastify";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 * Created At: 19 de 05 de 2026
 */

/**
 * Hook with state and logic for the "forgot password" flow.
 *
 * Drives a multi-step wizard (flow selection, OTP verification, new password,
 * success) for both the email and authenticator-app recovery flows. Manages the
 * OTP countdown timer, password-strength validation, and dispatches the reset
 * Redux thunks.
 *
 * @returns {Object} State and handlers consumed by the ForgotPassword UI.
 * @returns {Function} return.t Translation function (i18next).
 * @returns {Object|null} return.stepModel Active flow model (CHOICE/EMAIL/APP) or null.
 * @returns {number} return.activeStep Current step index within the flow.
 * @returns {boolean} return.emptyFields Whether a required field was left empty.
 * @returns {string} return.otp Current OTP input value.
 * @returns {Function} return.setOtp Setter for the OTP value.
 * @returns {Function} return.handleEmailChange Validates email format on change.
 * @returns {Function} return.handleEmailSend Submits the email and starts the flow.
 * @returns {Function} return.handleSelectFlow Selects a recovery flow.
 * @returns {Function} return.handleNavigateBack Navigates back one step/flow.
 * @returns {string} return.email Current email input value.
 * @returns {Function} return.setEmail Setter for the email value.
 * @returns {Function} return.handleKeyDown Submits email on Enter key.
 * @returns {boolean} return.isValidEmail Whether the email format is valid.
 * @returns {boolean} return.canResendOtp Whether the OTP can be resent.
 * @returns {string} return.minutes OTP timer minutes, zero-padded.
 * @returns {string} return.seconds OTP timer seconds, zero-padded.
 * @returns {Function} return.handleValidateOtp Verifies the entered OTP.
 * @returns {Function} return.setIsValidOtp Setter for OTP validity flag.
 * @returns {boolean} return.isValidOtp Whether the last OTP was valid.
 * @returns {Function} return.handleResendOtp Resends the OTP email.
 * @returns {boolean} return.loading Whether a request is in flight.
 * @returns {Function} return.handleSavePassword Submits the new password.
 * @returns {string} return.password Current password input value.
 * @returns {Function} return.setPassword Setter for the password value.
 * @returns {string} return.confirmPassword Current confirm-password value.
 * @returns {Function} return.setConfirmPassword Setter for confirm-password.
 * @returns {Function} return.setTouched Marks the password field as touched.
 * @returns {Function} return.barClass Returns CSS class for a strength bar segment.
 * @returns {boolean} return.allPassed Whether all password rules passed.
 * @returns {Array<Object>} return.ruleList Password rules with pass state and label.
 * @returns {Function} return.ruleClass Returns CSS class for a password rule row.
 * @returns {boolean} return.passwordsMatch Whether password and confirmation match.
 * @returns {boolean} return.canSave Whether the new password can be saved.
 * @returns {boolean} return.secretModal Whether the secret-key modal is open.
 * @returns {Function} return.setSecretModal Setter for the secret modal flag.
 * @returns {string|null} return.secretKey Recovery secret returned on success.
 * @returns {Function} return.setSecretKey Setter for the secret key.
 */
export const ForgotPasswordApp = () => {

    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [stepModel, setStepModel] = useState(null);
    const [activeStep, setActiveStep] = useState(STEP_MODEL.CHOICE.STEPS.VALIDATED);
    const [isValidOtp, setIsValidOtp] = useState(true);
    const [email, setEmail] = useState('');
    const [usernameRecovery, setUsernameRecovery] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [touched, setTouched] = useState(false);
    const [otp, setOtp] = useState('');
    const [emptyFields, setEmptyFields] = useState(false);
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [loading, setLoading] = useState(false);
    const OTP_TIME = 2 * 60;
    const [expiresOtp, setExpiresOtp] = useState(OTP_TIME);
    const [canResendOtp, setCanResendOtp] = useState(false);

    const [resetToken, setResetToken] = useState('');
    const [secretModal, setSecretModal] = useState(false);
    const [secretKey, setSecretKey] = useState(null);

    // Countdown timer for the OTP step. Runs only while the OTP step is active;
    // enables resend when it reaches zero, and is cleaned up on each tick/unmount.
    useEffect(() => {
        const isOtpStep = stepModel && activeStep === stepModel.STEPS.OTP;
        if (!isOtpStep) return;

        if (expiresOtp <= 0) {
            setCanResendOtp(true);
            return;
        }
        const interval = setInterval(() => {
            setExpiresOtp((prev) => prev - 1);
        }, 1000);
        return () => clearInterval(interval);
    }, [expiresOtp, activeStep, stepModel]);

    const minutes = String(Math.floor(expiresOtp / 60)).padStart(2, '0');
    const seconds = String(expiresOtp % 60).padStart(2, '0');

    /**
     * Validates the email format as the user types.
     * @param {React.ChangeEvent<HTMLInputElement>} e Input change event.
     */
    const handleEmailChange = (e) => {
        const value = e.target.value;
        setIsValidEmail(isEmailFormat(value));
    };

    /**
     * Submits the email when the Enter key is pressed.
     * @param {React.KeyboardEvent} e Keyboard event.
     */
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleEmailSend();
        }
    };

    /**
     * Selects a recovery flow and advances to its first step.
     *
     * CHOICE goes to the flow-picker step, EMAIL triggers an OTP email,
     * and APP jumps straight to OTP entry while (re)starting the timer.
     *
     * @param {Object} flow Flow model from STEP_MODEL (CHOICE/EMAIL/APP).
     */
    const handleSelectFlow = (flow) => {
        setStepModel(flow);

        if (flow?.NAME === STEP_MODEL.CHOICE.NAME) {
            setActiveStep(STEP_MODEL.CHOICE.STEPS.VALIDATED);
            return;
        }

        if (flow?.NAME === STEP_MODEL.EMAIL.NAME) {
            handleSendOtpEmail();
            return;
        }

        if (flow?.NAME === STEP_MODEL.APP.NAME) {
            setActiveStep(STEP_MODEL.APP.STEPS.OTP);
            setExpiresOtp(OTP_TIME);
            setCanResendOtp(false);
            return;
        }
    };

    /**
     * Dispatches the reset-email request and, on success, moves to the OTP step
     * and restarts the timer. On failure, returns to the CHOICE step and shows
     * the error.
     */
    const handleSendOtpEmail = () => {
        setLoading(true);
        dispatch(SendEmailToResetPassword(usernameRecovery || email))
            .then(({ payload } = {}) => {
                const { success, code } = payload?.data || {};
                setLoading(false);
                if (success) {
                    setActiveStep(STEP_MODEL.EMAIL.STEPS.OTP);
                    setExpiresOtp(OTP_TIME);
                    setCanResendOtp(false);
                    toast.success(t('send_otp_by_email'));
                }
                else {
                    setStepModel(STEP_MODEL.CHOICE);
                    setActiveStep(STEP_MODEL.CHOICE.STEPS.VALIDATED);
                    errorMsg(code);
                }
            });
    };

    /**
     * Navigates back one level in the flow.
     *
     * With no flow selected, leaves to the home route. From CHOICE, clears the
     * flow. From the OTP step, returns to CHOICE. From a later step, resets OTP
     * state and the timer and returns to the OTP step.
     */
    const handleNavigateBack = () => {
        if (!stepModel) {
            navigate(routes.home);
            return;
        }
        if (stepModel.NAME === STEP_MODEL.CHOICE.NAME) {
            setStepModel(null);
            return;
        }
        if (activeStep === stepModel.STEPS.OTP) {
            setStepModel(STEP_MODEL.CHOICE);
            setActiveStep(STEP_MODEL.CHOICE.STEPS.VALIDATED);
            return;
        }
        setOtp('');
        setIsValidOtp(true);
        setEmptyFields(false);
        setResetToken('');
        setExpiresOtp(OTP_TIME);
        setCanResendOtp(false);
        setActiveStep(stepModel.STEPS.OTP);
    };


    /**
     * Validates the email input, stores it as the recovery username, and starts
     * the CHOICE flow. Flags empty/invalid input instead of advancing.
     */
    const handleEmailSend = () => {
        if (email === '' || !isValidEmail) {
            setEmptyFields(true);
            return;
        }
        setEmptyFields(false);
        setUsernameRecovery(email);
        handleSelectFlow(STEP_MODEL.CHOICE);
    };

    /**
     * Resends the OTP email once the timer allows it. On success, restarts the
     * timer and clears the current OTP input.
     */
    const handleResendOtp = () => {
        if (!canResendOtp) return;
        setLoading(true);

        dispatch(SendEmailToResetPassword(usernameRecovery))
            .then(({ payload } = {}) => {
                const { success, code } = payload?.data || {};
                setLoading(false);
                if (success) {
                    setExpiresOtp(OTP_TIME);
                    setCanResendOtp(false);
                    setOtp('');
                    toast.success(t('send_otp_by_email'));
                }
                else {
                    errorMsg(code);
                }
            });
    };

    /**
     * Verifies the entered OTP for the active flow (APP or EMAIL). On success,
     * stores the reset token and advances to the create-password step; on
     * failure, marks the OTP invalid and shows the error.
     */
    const handleValidateOtp = () => {
        if (!otp) {
            setEmptyFields(true);
            setIsValidOtp(false);
            return toast.error(t('error_otp'));
        }
        setLoading(true);
        const isApp = stepModel?.NAME === STEP_MODEL.APP.NAME;
        dispatch(VerifyOtp({username: usernameRecovery || email, otp, type: isApp ? STEP_MODEL.APP.NAME :  STEP_MODEL.EMAIL.NAME }))
            .then(({ payload } = {}) => {
                const { success, code, data: token } = payload?.data || {};
                setLoading(false);
                if (success) {
                    setIsValidOtp(true);
                    setResetToken(token);
                    setActiveStep(
                        isApp
                            ? STEP_MODEL.APP.STEPS.CREATE_PASSWORD
                            : STEP_MODEL.EMAIL.STEPS.CREATE_PASSWORD
                    );
                    toast.success(t('correct_otp'));
                }
                else {
                    setIsValidOtp(false);
                    errorMsg(code);
                }
            });
    };

    /**
     * Password-strength checks, recomputed when the password changes.
     * @type {{minLength: boolean, hasNumber: boolean, hasUppercase: boolean, hasSpecial: boolean}}
     */
    const checks = useMemo(() => ({
        minLength: password.length >= 8,
        hasNumber: /[0-9]/.test(password),
        hasUppercase: /[A-Z]/.test(password),
        hasSpecial: /[^A-Za-z0-9]/.test(password),
    }), [password]);

    const ruleList = [
        { ok: checks.minLength, label: 'password_min_length' },
        { ok: checks.hasNumber, label: 'password_number' },
        { ok: checks.hasUppercase, label: 'password_uppercase' },
        { ok: checks.hasSpecial, label: 'password_special_character' },
    ];

    const passedCount = Object.values(checks).filter(Boolean).length;
    const allPassed = passedCount === 4;
    const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;
    const canSave = allPassed && passwordsMatch;

    /**
     * Resolves the CSS class for a password-rule row based on its pass state.
     * @param {boolean} ok Whether the rule is satisfied.
     * @returns {string} CSS class name (success / error / neutral).
     */
    const ruleClass = (ok) => {
        if (ok) return 'font-color-success-600';
        if (touched && password.length > 0) return 'font-color-error-600';
        return 'font-color-shades-700';
    };

    /**
     * Resolves the CSS class for a strength-bar segment.
     * @param {number} index Zero-based segment index.
     * @returns {string} CSS class name (filled / empty).
     */
    const barClass = (index) => {
        if (passedCount > index) return 'border-full-success-500-2px';
        return 'border-full-shades-300-2px';
    };

    /**
     * Submits the new password using the reset token. On success, advances to
     * the success step and opens the secret-key modal; on failure, shows the
     * error. No-op when the password does not meet all rules.
     */
    const handleSavePassword = () => {
        if (!canSave) return;
        setLoading(true);
        const isApp = stepModel?.NAME === STEP_MODEL.APP.NAME;
        dispatch(NewPassword({ resetToken, newPassword: password }))
            .then(({ payload } = {}) => {
                const { success, code, data } = payload?.data || {};
                setLoading(false);
                if (success) {
                    setActiveStep(
                        isApp
                            ? STEP_MODEL.APP.STEPS.SUCCESS
                            : STEP_MODEL.EMAIL.STEPS.SUCCESS
                    );
                    setSecretModal(true);
                    setSecretKey(data?.secret);
                } else {
                    errorMsg(code);
                }
            });
    };

    return {
        t,
        stepModel,
        activeStep,
        emptyFields,
        otp,
        setOtp,
        handleEmailChange,
        handleEmailSend,
        handleSelectFlow,
        handleNavigateBack,
        email,
        setEmail,
        handleKeyDown,
        isValidEmail,
        canResendOtp,
        minutes,
        seconds,
        handleValidateOtp,
        setIsValidOtp,
        isValidOtp,
        handleResendOtp,
        loading,
        handleSavePassword,
        password,
        setPassword,
        confirmPassword,
        setConfirmPassword,
        setTouched,
        barClass,
        allPassed,
        ruleList,
        ruleClass,
        passwordsMatch,
        canSave,
        secretModal,
        setSecretModal,
        secretKey,
        setSecretKey
    };
};
